import gql from "graphql-tag";

export const COACH_QUERY = gql`
  query coach($id: ID!) {
    coach(id: $id) {
      firstName
      lastName
      userProfile {
        id
        assessmentBookingLink
      }
    }
  }
`;

export const COACH_TASK_NUMBERS = gql`
  query coachTaskNumbers($id: ID!) {
    coachTaskNumbers(coachId: $id) {
      id
      firstName
      lastName
      unresolvedChats
      unresolvedTasks
    }
  }
`;

export const COACH_FEELING_QUERY = gql`
  query coachFeeling($id: ID!, $today: Date) {
    coachFeeling(coachId: $id, feelingDate: $today) {
      feelingIndex
    }
  }
`;

export const COACHING_CLIENT_NUMBERS_QUERY = gql`
  query coachingSubscriptionNumbers($userId: ID!) {
    coachingSubscriptionNumbers(userId: $userId) {
      onboarding {
        subscriptionCount
        unresolvedChats
      }
      oneOnOne {
        subscriptionCount
        unresolvedChats
      }
      club {
        subscriptionCount
        unresolvedChats
      }
      package
      clientSitting {
        subscriptionCount
        unresolvedChats
      }
      grad {
        subscriptionCount
        unresolvedChats
      }
      offboarding
    }
  }
`;

export const SET_COMPLETED_QUERY = gql`
  mutation setCoachActivity(
    $userProgramId: ID
    $dayHalf: String!
    $completed: Boolean!
    $subscriptionId: ID
    $coachId: ID
  ) {
    setCoachActivity(
      userProgramId: $userProgramId
      dayHalf: $dayHalf
      completed: $completed
      subscriptionId: $subscriptionId
      coachId: $coachId
    ) {
      ok
    }
  }
`;

export const SET_IMPACT_MUTATION = gql`
  mutation coachImpact($coachId: ID!, $clientId: ID!, $impactDate: Date, $didImpact: Boolean!) {
    coachImpact(coachId: $coachId, clientId: $clientId, impactDate: $impactDate, didImpact: $didImpact) {
      ok
    }
  }
`;

export const SET_FEELING_MUTATION = gql`
  mutation coachFeeling($coachId: ID!, $feelingDate: Date, $feelingIndex: Int!) {
    coachFeeling(coachId: $coachId, feelingDate: $feelingDate, feelingIndex: $feelingIndex) {
      ok
    }
  }
`;

// coachId is float and not int: Possibly related to https://github.com/graphql-python/graphene-django/pull/682
export const COACH_SUMMARY_METRIC_HISTORY = gql`
  query coachSummaryMetricHistory($metricDate_Gte: Date, $coachId: Float) {
    coachSummaryMetricHistory(metricDate_Gte: $metricDate_Gte, coachId: $coachId) {
      edges {
        node {
          id
          metricDate
          numberClientsOver50
          numberClientsNoPics
          numberClientsNoWorkout
          averageNumber
          averageLabel
          coach {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const COACH_METRIC_HISTORY = gql`
  query coachMetricHistory($coachId: Int!, $dateFrom: Date!) {
    coachMetricHistory(coachId: $coachId, dateFrom: $dateFrom) {
      id
      fullName
      metricList {
        numberMessages
        numberNutritionPics
        exerciseLogged
        metricNumber
        sevenDayAverageNumber
        metricDate
      }
    }
  }
`;

export const COMPANY_METRIC_HISTORY = gql`
  query companyMetricHistory($dateFrom: Date!) {
    companyMetricHistory(dateFrom: $dateFrom) {
      metricDate
      percentageOnTrack
      numberActiveClients
      numberClientsOver50
      numberClientsNoPics
      numberClientsNoWorkout
      averageNumber
    }
  }
`;

export const COACHING_SUBSCRIPTION_QUERY = gql`
  query userSubscriptions(
    $productType: String
    $stage: String
    $stages: [String]
    $coachId: ID
    $packageCoachId: ID
    $secondaryCoachId: ID
    $product_ProgramType: String
    $selectedDate: Date
    $orderBy: String
    $prizeDate: Date!
  ) {
    userSubscriptions(
      product_Type: $productType
      stage_In: $stages
      stage: $stage
      coachId: $coachId
      billingOrder_Package_CoachId: $packageCoachId
      secondaryCoachId: $secondaryCoachId
      active: true
      product_ProgramType: $product_ProgramType
      orderBy: $orderBy
    ) {
      edges {
        node {
          id
          startDate
          endDate
          weeksOnProgram
          stage
          active
          onboardingCompleted
          submittedMedicalForm
          programStartDate
          lastMessageSent
          hasCheckinGoal
          hasBhag
          clientInformation {
            id
            dateOfBirth
            height
            phone
            city
            occupation
            userStatus
            suburb
            gender
            profilePicture
            hubspotContactVid
            dietaryRequirement
            whatsappJid
            alternateWhatsappJid
            fitnessLevel
            weight
            desiredWeight
            desiredWeightLoss
            occupation
            company
            pregnant
            companyRole
            companySize
            hasMedicalConditions
            requiredSupport
            calculatedServingSize
            calories
            taskPauseUntil
            taskPauseNote
            fitnessLevel
            user {
              id
              email
              firstName
              lastName
              username
              lastLogin
              dateJoined
              usernutritionpreferenceSet {
                id
                nutritionPreference {
                  id
                  title
                  isPrimary
                }
              }
              usertagSet {
                id
                tag {
                  id
                  title
                  code
                }
              }
              userProfile {
                id
                lastActive
                coachingChat {
                  id
                }
                activeSubscription {
                  id
                  coach {
                    id
                    firstName
                    lastName
                  }
                }
              }
            }
            country {
              name
              iocCode
              code
            }
          }
          prizeEligibility(anchorDate: $prizeDate) {
            qualifiedForPrize
            dayBreakdown {
              date
              isRestDay
              workoutImage
              loggedNutrition
              loggedWorkout
              greenDay
            }
          }
          coachChat {
            id
            title
            resolved
            lastMessage {
              id
              text
              sentDate
              user {
                id
                firstName
                lastName
                isStaff
              }
              chat {
                id
              }
            }
            last3Messages {
              id
              text
              sentDate
              user {
                id
                firstName
                lastName
              }
            }
          }
          upcomingPayment {
            type
            date
          }
          billingOrder {
            id
            nextBillingDate
          }
          metric {
            id
            numberNutritionPics
            exerciseLogged
            numberMessages
          }
          coachactivitySet(forSelectedDate: $selectedDate) {
            edges {
              node {
                id
                dayHalf
                completedAt
              }
            }
          }
          product {
            id
            name
          }
          transitionProductVariant {
            id
            billingPeriod
            product {
              id
              title
            }
          }
          coachingAssessments {
            id
            requiredDate
            assessmentDate
          }
          secondaryCoachSubscriptions(period_StartDate_Lte: $selectedDate, periodGraceEndDate: $selectedDate) {
            edges {
              node {
                id
                addedToWhatsapp
                period {
                  id
                  startDate
                  endDate
                  coach {
                    id
                    firstName
                    lastName
                  }
                }
              }
            }
          }
          user {
            id
            firstName
            lastName
            lastLogin
            email
            username
            subscriptionblockSet {
              id
              currentDay
              startDate
              endDate
              block {
                id
                title
                duration
              }
            }
            subscriptionnutritionblockSet(startDate_Lte: $selectedDate) {
              edges {
                node {
                  id
                  startDate
                  nutritionBlock {
                    id
                    title
                  }
                  subscriptionnutritionblockrecipeSet(date: $selectedDate) {
                    edges {
                      node {
                        id
                        meal
                        recipe {
                          id
                          title
                          meal
                          description
                        }
                      }
                    }
                  }
                  nutritionBlock {
                    id
                    title
                  }
                }
              }
            }
            userProfile {
              id
              phone
              dateOfBirth
              profilePicture
              lastActive
              lastCoachingAssessmentDate
              lastUserAssessmentDate
              engagementScore
              supportChat {
                id
              }
              coachingChat {
                id
                lastMessage {
                  id
                  createdAt
                  user {
                    id
                    isStaff
                  }
                }
                userchatSet {
                  id
                  user {
                    id
                  }
                  lastViewedTimestamp
                }
              }
              nutritionPlan {
                id
                name
                pdf
              }
              coachingNutritionPlan {
                id
                servingSize
                dietaryRestriction
                pdf
              }
            }
          }
        }
      }
    }
  }
`;

export const SUBSCRIPTION_ACTIVITY_QUERY = gql`
  query subscriptionActivityQuery($userId: ID, $username: String, $startDate_Lte: Date, $endDate_Gte: Date) {
    workoutSubscriptionBlocks(
      userId: $userId
      user_Username: $username
      startDate_Lte: $startDate_Lte
      endDate_Gte: $endDate_Gte
    ) {
      edges {
        node {
          id
          currentDay
          progressPercent
          startDate
          logCount
          originalStartDate
          endDate
          isSelfPaced
          block {
            id
            title
            type
            type
            unitSet {
              id
              title
              order
              unitactivitySet {
                id
                order
                currentDay
                type
                activity {
                  id
                  title
                  description
                }
              }
            }
          }
        }
      }
    }
    nutritionSubscriptionBlocks(userId: $userId) {
      edges {
        node {
          id
          startDate
          active
          nutritionBlock {
            id
            title
          }
        }
      }
    }
  }
`;

export const COACHING_ONBOARDING_SUBSCRIPTION_QUERY = gql`
  query userSubscriptions(
    $productType: String
    $stage: String
    $coachId: ID
    $product_ProgramType: String
    $selectedDate: Date
  ) {
    userSubscriptions(
      product_Type: $productType
      stage: $stage
      coachId: $coachId
      active: true
      product_ProgramType: $product_ProgramType
    ) {
      edges {
        node {
          id
          startDate
          endDate
          stage
          active
          onboardingCompleted
          submittedMedicalForm
          programStartDate
          serviceStartDate
          hasCheckinGoal
          hasBhag
          coachChat {
            id
            title
            resolved
            lastMessage {
              id
              text
              sentDate
              user {
                id
                firstName
                lastName
                isStaff
              }
            }
          }
          product {
            id
            name
          }
          coachactivitySet(forSelectedDate: $selectedDate) {
            edges {
              node {
                id
                dayHalf
                completedAt
              }
            }
          }
          coachingAssessments {
            id
            requiredDate
            assessmentDate
          }
          user {
            id
            firstName
            lastName
            lastLogin
            email
            username
            lastLogin
            subscriptionblockSet {
              id
              startDate
              endDate
              currentDay
              block {
                id
                title
                duration
              }
            }
            subscriptionnutritionblockSet(startDate_Lte: $selectedDate) {
              edges {
                node {
                  id
                  startDate
                  nutritionBlock {
                    id
                    title
                  }
                  subscriptionnutritionblockrecipeSet(date: $selectedDate) {
                    edges {
                      node {
                        id
                        meal
                        recipe {
                          id
                          title
                          meal
                          description
                        }
                      }
                    }
                  }
                  nutritionBlock {
                    id
                    title
                  }
                }
              }
            }
            userProfile {
              id
              dateOfBirth
              profilePicture
              coachingChat {
                id
                resolved
                lastMessage {
                  id
                  text
                  sentDate
                  user {
                    id
                    firstName
                    lastName
                    isStaff
                  }
                  chat {
                    id
                  }
                }
                userchatSet {
                  id
                  user {
                    id
                  }
                  lastViewedTimestamp
                }
              }
              nutritionPlan {
                id
                name
                pdf
              }
              coachingNutritionPlan {
                id
                servingSize
                dietaryRestriction
                pdf
              }
            }
          }
        }
      }
    }
  }
`;

export const COACHING_OFFBOARDING_SUBSCRIPTION_QUERY = gql`
  query userSubscriptions(
    $productType: String
    $stages: [String]
    $coachId: ID
    $product_ProgramType: String
    $selectedDate: Date
  ) {
    userSubscriptions(
      product_Type: $productType
      stage_In: $stages
      coachId: $coachId
      product_ProgramType: $product_ProgramType
      offboardingDateFilter: $selectedDate
    ) {
      edges {
        node {
          id
          startDate
          endDate
          stage
          active
          cancellationRequestDate
          finalCheckinDone
          transitionProductVariant {
            id
            product {
              id
              title
            }
          }
          likelyToReturnOneOnOne
          clientRemovedMainWaGroup
          botRemovedClientWaGroupChatArchived
          finalMessageSent
          notesOnWhyLeft
          offboardingCompleted
          user {
            id
            firstName
            lastName
            lastLogin
            email
            username
            lastLogin
            userProfile {
              id
              dateOfBirth
              profilePicture
            }
          }
        }
      }
    }
  }
`;

export const SET_OFFBOARDING_MUTATION = gql`
  mutation setOffboarding($input: OffboardingInput!) {
    setOffboardingMutation(input: $input) {
      success
      error
    }
  }
`;

export const SIMPLE_COACHING_SUBSCRIPTION_QUERY = gql`
  query userSubscriptions($productType: String, $coachId: ID, $active: Boolean) {
    userSubscriptions(product_Type: $productType, coachId: $coachId, active: $active) {
      edges {
        node {
          id
          user {
            id
            email
          }
        }
      }
    }
  }
`;

export const MOVE_COACH_CLIENTS_MUTATION = gql`
  mutation moveCoachClientsMutation($data: MoveCoachClientsInput!) {
    moveCoachClientsMutation(data: $data) {
      success
    }
  }
`;

export const MESSAGES_AFTER_QUERY = gql`
  query messagesAfter($ago: Int!, $chats: [ID]!) {
    messagesAfter(ago: $ago, chats: $chats) {
      id
      chat {
        id
        resolved
      }
      user {
        id
        firstName
        lastName
        userProfile {
          id
          userType
        }
      }
      text
    }
  }
`;
